import React from 'react';
import { FiSmartphone,  FiCheckCircle, FiClock, FiUsers, FiZap,  FiGlobe, FiShield } from 'react-icons/fi';

const MobileFirst = () => {
  const mobileReasons = [
    {
      icon: <FiSmartphone className="w-6 h-6" />,
      title: 'Most Teachers & Staff Use Mobile as Primary Device',
      description: 'In Indian institutions, teachers and administrative staff often use mobile phones as their primary computing device. They check attendance, respond to parents, and manage tasks from their phones. If your software is not mobile-friendly, they will struggle to use it.',
      stat: '70% of teachers access school systems via mobile phones',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Parents Expect Mobile Access',
      description: 'Parents want to check their child\'s attendance, grades, and announcements from their phones. They don\'t want to sit at a computer. If your parent portal is not mobile-friendly, parents will be frustrated and disengaged.',
      stat: '85% of parents prefer mobile access to school systems',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiGlobe className="w-6 h-6" />,
      title: 'India\'s Internet Usage is Mobile-First',
      description: 'More than 80% of internet users in India access the web primarily through mobile devices. Most people never use desktop computers. Your ERP must work perfectly on mobile to serve this audience.',
      stat: '80%+ of Indian internet users are mobile-first',
      color: 'bg-amber-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Access Anytime, Anywhere',
      description: 'Teachers and administrators need to access the system from anywhere—classrooms, staff rooms, homes, and even while traveling. Mobile-first design ensures they can use the full system on any device.',
      stat: '90% of teachers want to access the system from anywhere',
      color: 'bg-rose-50'
    },
    {
      icon: <FiZap className="w-6 h-6" />,
      title: 'Better Adoption and Engagement',
      description: 'When software is mobile-friendly, people actually use it. Teachers are more likely to mark attendance, enter grades, and communicate with parents. Engagement improves, and the institution gets better data.',
      stat: 'Mobile-friendly software has 60% higher user adoption',
      color: 'bg-purple-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'Future-Proof Your Institution',
      description: 'The trend toward mobile is only accelerating. Students, parents, and teachers will increasingly use mobile devices. Institutions that adopt mobile-first software today will be better prepared for the future.',
      stat: '95% of institutions plan to invest in mobile capabilities',
      color: 'bg-blue-50'
    }
  ];

  const mobileFeatures = [
    {
      title: 'Responsive Interface',
      description: 'Works perfectly on any screen size—phone, tablet, or desktop. No zooming, no scrolling issues.'
    },
    {
      title: 'Touch-Friendly Controls',
      description: 'Buttons, forms, and navigation are designed for touch. Easy to use with fingers on small screens.'
    },
    {
      title: 'Offline Capabilities',
      description: 'Teachers can mark attendance offline. Data syncs when internet connectivity is restored.'
    },
    {
      title: 'Push Notifications',
      description: 'Real-time notifications sent to mobile devices. Parents and teachers stay informed instantly.'
    },
    {
      title: 'Mobile Analytics',
      description: 'Track mobile usage and engagement. Understand how users interact with the system on mobile.'
    },
    {
      title: 'Fast Loading',
      description: 'Optimized for low bandwidth and slower connections. Works even in areas with limited internet.'
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Walk into any school, college, or coaching centre in India today, and you'll see the same scene—teachers glued to their phones, parents scrolling through WhatsApp, and staff managing everything from their mobile devices. In India, the primary computing device is no longer a desktop computer. It's a smartphone.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        <strong className="text-slate-900">This reality has profound implications for educational management software.</strong> If your ERP is not designed for mobile, you are asking your teachers, staff, and parents to work against their natural behavior. You are creating frustration, reducing adoption, and missing opportunities.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here's why mobile-first design matters for Indian educational management software—and why it's essential for success.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">70%</div>
          <div className="text-sm text-slate-500">Teachers use mobile as primary device</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">85%</div>
          <div className="text-sm text-slate-500">Parents prefer mobile access</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">80%+</div>
          <div className="text-sm text-slate-500">Indian internet users are mobile-first</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">Why Mobile-First Design Matters</h2>

      {/* Mobile Reasons */}
      {mobileReasons.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">📊 {item.stat}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Mobile Features */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">What Mobile-First Design Looks Like</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {mobileFeatures.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-start gap-2">
                <FiCheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600 mt-1">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Desktop-Only vs. Mobile-First ERP</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Desktop-Only ERP</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Requires desktop computer</span></li>
              <li className="flex items-start gap-2">• <span>Difficult to use on mobile</span></li>
              <li className="flex items-start gap-2">• <span>Limited access from anywhere</span></li>
              <li className="flex items-start gap-2">• <span>Low user adoption</span></li>
              <li className="flex items-start gap-2">• <span>Teachers struggle to use</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Mobile-First ERP (Truyon)</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Works on any device</span></li>
              <li className="flex items-start gap-2">• <span>Perfect mobile experience</span></li>
              <li className="flex items-start gap-2">• <span>Accessible from anywhere</span></li>
              <li className="flex items-start gap-2">• <span>High user adoption</span></li>
              <li className="flex items-start gap-2">• <span>Teachers love using it</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* The Teacher Experience */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">The Teacher Experience</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          Teachers are busy. They don't have time to sit at a desktop computer in a staff room to mark attendance or check messages. They need to do these tasks quickly from their phones, between classes, or from anywhere.
        </p>
        <p className="text-amber-800 leading-relaxed mb-3">
          A mobile-first ERP makes this possible. Teachers can mark attendance in 30 seconds from their phones. They can respond to parent queries instantly. They can enter grades while walking to their next class. This reduces frustration and saves precious time.
        </p>
        <p className="text-amber-800 leading-relaxed">
          <strong>Mobile-first is not just about convenience. It's about enabling teachers to do their best work.</strong>
        </p>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon is the Best Mobile-First ERP</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📱</div>
            <h4 className="font-bold text-slate-900 text-sm">Mobile-First Design</h4>
            <p className="text-xs text-slate-600">Built for mobile from the ground up</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">⚡</div>
            <h4 className="font-bold text-slate-900 text-sm">Fast Loading</h4>
            <p className="text-xs text-slate-600">Works on slow networks</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📶</div>
            <h4 className="font-bold text-slate-900 text-sm">Offline Capability</h4>
            <p className="text-xs text-slate-600">Works without internet</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔔</div>
            <h4 className="font-bold text-slate-900 text-sm">Push Notifications</h4>
            <p className="text-xs text-slate-600">Real-time alerts on mobile</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">👆</div>
            <h4 className="font-bold text-slate-900 text-sm">Touch-Friendly</h4>
            <p className="text-xs text-slate-600">Designed for fingers, not mice</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🌐</div>
            <h4 className="font-bold text-slate-900 text-sm">Any Device</h4>
            <p className="text-xs text-slate-600">Works on any phone or tablet</p>
          </div>
        </div>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Go Mobile-First with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          India is a mobile-first country. Your educational management software must reflect this reality. If your ERP is not designed for mobile, you are creating frustration for your teachers, staff, and parents.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon is built mobile-first. Responsive design, touch-friendly controls, offline capabilities, and real-time push notifications. Teachers and staff can use it from anywhere. Parents can stay informed on their phones.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for mobile-first ERP.</strong> Designed for the way India actually works.
        </p>
      </div>
    </>
  );
};

export default MobileFirst;